// Tagihan Menunggak report generator: PDF (jsPDF + autoTable) and Word (docx).
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell,
  HeadingLevel, AlignmentType, TextRun, ImageRun, WidthType, BorderStyle,
} from "docx";

const STATUS_LABEL = {
  pending: "Belum Ditagih",
  ditagih: "Sudah Ditagih",
  janji_bayar: "Janji Bayar",
  lunas: "Lunas",
  tidak_ketemu: "Tidak Ketemu",
};

const fmtStatus = (it) => {
  const s = it.status || "pending";
  const lbl = STATUS_LABEL[s] || "Belum Ditagih";
  if (s === "janji_bayar" && it.promised_date) return `${lbl} (${it.promised_date})`;
  return lbl;
};

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const slugify = (s) => String(s || "tunggakan").replace(/[^\w-]+/g, "_");

const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

const resizeForPdf = async (dataUrl, maxWmm, maxHmm) => {
  try {
    const img = await loadImage(dataUrl);
    const ratio = img.width / img.height;
    let wmm = maxWmm;
    let hmm = wmm / ratio;
    if (hmm > maxHmm) { hmm = maxHmm; wmm = hmm * ratio; }
    const pxPerMm = 150 / 25.4;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(wmm * pxPerMm));
    canvas.height = Math.max(1, Math.round(hmm * pxPerMm));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/jpeg", 0.85), width: wmm, height: hmm };
  } catch { return null; }
};

const resizeForDocx = async (dataUrl, maxWpx, maxHpx) => {
  try {
    const img = await loadImage(dataUrl);
    const ratio = img.width / img.height;
    let w = maxWpx;
    let h = w / ratio;
    if (h > maxHpx) { h = maxHpx; w = h * ratio; }
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w));
    canvas.height = Math.max(1, Math.round(h));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    const out = canvas.toDataURL("image/jpeg", 0.85);
    const b64 = out.split(",")[1];
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { bytes, width: Math.round(w), height: Math.round(h) };
  } catch { return null; }
};

const buildSummary = (items) => {
  const counts = items.reduce((acc, it) => {
    const s = it.status || "pending";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const totalTagihan = items.reduce((sum, it) => sum + Number(it.total_tagihan || 0), 0);
  return { counts, totalTagihan };
};

// =============== PDF EXPORT ===============
export const exportTunggakanPdf = async (list, photoSize = "medium") => {
  const items = list?.items || [];
  const name = list?.name || "Tagihan Menunggak";
  const updated = list?.updated_at
    ? new Date(list.updated_at).toLocaleString("id-ID")
    : new Date().toLocaleString("id-ID");

  const SIZE_MM = { none: 0, small: 18, medium: 30, large: 45 };
  const photoMm = SIZE_MM[photoSize] ?? 30;
  const { counts, totalTagihan } = buildSummary(items);

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(name, 14, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Total ${items.length} pelanggan · Total tagihan ${fmtRp(totalTagihan)} · diperbarui ${updated}`, 14, 22);

  const summary = [
    `Belum: ${counts.pending || 0}`,
    `Ditagih: ${counts.ditagih || 0}`,
    `Janji Bayar: ${counts.janji_bayar || 0}`,
    `Lunas: ${counts.lunas || 0}`,
    `Tidak Ketemu: ${counts.tidak_ketemu || 0}`,
  ].join("   ·   ");
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(summary, 14, 27);
  doc.setTextColor(0);

  const photoCache = new Map();
  if (photoMm > 0) {
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.photo && typeof it.photo === "string" && it.photo.startsWith("data:image/")) {
        const r = await resizeForPdf(it.photo, photoMm, photoMm);
        if (r) photoCache.set(i, r);
      }
    }
  }

  const head = photoMm > 0
    ? [["No", "ID", "Nama", "Alamat", "Bln", "Total Tagihan", "Status", "Catatan", "Foto"]]
    : [["No", "ID", "Nama", "Alamat", "Bln", "Total Tagihan", "Status", "Catatan"]];

  const body = items.map((it, i) => {
    const row = [
      String(i + 1),
      it.customer_id || "",
      it.nama || "-",
      it.alamat || "-",
      String(it.jml_bulan || 0),
      fmtRp(it.total_tagihan),
      fmtStatus(it),
      it.note || "",
    ];
    if (photoMm > 0) row.push("");
    return row;
  });

  autoTable(doc, {
    head, body, startY: 32,
    margin: { left: 10, right: 10 },
    styles: { fontSize: 8, cellPadding: 1.5, valign: "middle", overflow: "linebreak" },
    headStyles: { fillColor: [190, 24, 93], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 8, halign: "center" },
      1: { cellWidth: 24, font: "courier" },
      2: { cellWidth: 40 },
      3: { cellWidth: 55 },
      4: { cellWidth: 10, halign: "center" },
      5: { cellWidth: 28, halign: "right" },
      6: { cellWidth: 26, halign: "center" },
      7: { cellWidth: photoMm > 0 ? 30 : 45 },
      ...(photoMm > 0 ? { 8: { cellWidth: photoMm + 4, halign: "center" } } : {}),
    },
    didParseCell: (data) => {
      if (photoMm > 0 && data.section === "body" && data.column.index === 8) {
        data.cell.styles.minCellHeight = photoMm + 2;
      }
    },
    didDrawCell: (data) => {
      if (photoMm > 0 && data.section === "body" && data.column.index === 8) {
        const cached = photoCache.get(data.row.index);
        if (cached) {
          const cx = data.cell.x + (data.cell.width - cached.width) / 2;
          const cy = data.cell.y + (data.cell.height - cached.height) / 2;
          try { doc.addImage(cached.dataUrl, "JPEG", cx, cy, cached.width, cached.height); } catch {/* ignore */}
        }
      }
    },
  });

  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Halaman ${p} / ${pageCount}`,
      doc.internal.pageSize.getWidth() - 10,
      doc.internal.pageSize.getHeight() - 6, { align: "right" });
  }

  doc.save(`${slugify(name)}.pdf`);
};

// =============== WORD (.docx) EXPORT ===============
export const exportTunggakanDocx = async (list, photoSize = "medium") => {
  const items = list?.items || [];
  const name = list?.name || "Tagihan Menunggak";
  const updated = list?.updated_at
    ? new Date(list.updated_at).toLocaleString("id-ID")
    : new Date().toLocaleString("id-ID");

  const SIZE_PX = { none: 0, small: 70, medium: 120, large: 180 };
  const photoPx = SIZE_PX[photoSize] ?? 120;
  const { counts, totalTagihan } = buildSummary(items);

  const headerCells = ["No", "ID", "Nama", "Alamat", "Bln", "Total Tagihan", "Status", "Catatan"];
  if (photoPx > 0) headerCells.push("Foto");

  const border = { style: BorderStyle.SINGLE, size: 4, color: "BFBFBF" };
  const borders = { top: border, bottom: border, left: border, right: border };

  const headerRow = new TableRow({
    tableHeader: true,
    children: headerCells.map((t) =>
      new TableCell({
        borders,
        shading: { fill: "BE185D" },
        children: [new Paragraph({ alignment: AlignmentType.CENTER, children: [new TextRun({ text: t, bold: true, color: "FFFFFF" })] })],
      })),
  });

  const rows = [headerRow];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const cells = [
      new TableCell({ borders, children: [new Paragraph({ alignment: AlignmentType.CENTER, text: String(i + 1) })] }),
      new TableCell({ borders, children: [new Paragraph({ children: [new TextRun({ text: it.customer_id || "", font: "Courier New" })] })] }),
      new TableCell({ borders, children: [new Paragraph(it.nama || "-")] }),
      new TableCell({ borders, children: [new Paragraph(it.alamat || "-")] }),
      new TableCell({ borders, children: [new Paragraph({ alignment: AlignmentType.CENTER, text: String(it.jml_bulan || 0) })] }),
      new TableCell({ borders, children: [new Paragraph(fmtRp(it.total_tagihan))] }),
      new TableCell({ borders, children: [new Paragraph({ alignment: AlignmentType.CENTER, text: fmtStatus(it) })] }),
      new TableCell({ borders, children: [new Paragraph(it.note || "")] }),
    ];
    if (photoPx > 0) {
      let imgPara = new Paragraph({ alignment: AlignmentType.CENTER, text: "" });
      if (it.photo && typeof it.photo === "string" && it.photo.startsWith("data:image/")) {
        const r = await resizeForDocx(it.photo, photoPx, photoPx);
        if (r) {
          imgPara = new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new ImageRun({ data: r.bytes, transformation: { width: r.width, height: r.height } })],
          });
        }
      }
      cells.push(new TableCell({ borders, children: [imgPara] }));
    }
    rows.push(new TableRow({ children: cells }));
  }

  const table = new Table({ rows, width: { size: 100, type: WidthType.PERCENTAGE } });

  const doc = new Document({
    sections: [{
      properties: {},
      children: [
        new Paragraph({ heading: HeadingLevel.HEADING_1, children: [new TextRun({ text: name, bold: true, color: "BE185D" })] }),
        new Paragraph({ children: [new TextRun({ text: `Total ${items.length} pelanggan · Total tagihan ${fmtRp(totalTagihan)} · diperbarui ${updated}`, italics: true, color: "595959" })] }),
        new Paragraph({ children: [new TextRun({
          text: `Belum: ${counts.pending || 0}   ·   Ditagih: ${counts.ditagih || 0}   ·   Janji Bayar: ${counts.janji_bayar || 0}   ·   Lunas: ${counts.lunas || 0}   ·   Tidak Ketemu: ${counts.tidak_ketemu || 0}`,
          color: "595959" })] }),
        new Paragraph({ text: "" }),
        table,
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${slugify(name)}.docx`);
};
